import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDaysIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon, Clock3Icon, CopyIcon, DownloadIcon, MenuIcon, PlusIcon, SendIcon, UsersRoundIcon } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { downloadCsv } from '../data/csv';
import { EntryNoteDialog } from '../components/timesheets/EntryNoteDialog';
import { PinnedAllocationDialog } from '../components/timesheets/PinnedAllocationDialog';
import { RemoveEntryTypeDialog } from '../components/timesheets/RemoveEntryTypeDialog';
import { TimesheetGrid } from '../components/timesheets/TimesheetGrid';
import { emptyHours, getRowTotal, parseHours, WEEKDAY_KEYS, type DayKey, type ProjectOption, type TimesheetAllocationTemplate, type TimesheetRow } from '../data/timesheetTypes';
import { useSettingsStore } from '../data/settingsStore';
import { Sidebar } from '../components/Sidebar';
import { INITIAL_JOBS } from './Jobs';
import { useScreenInit } from '../useScreenInit';
type Feedback = {
  tone: 'saved' | 'submitted';
  message: string;
};
type SubmittedWeek = {
  submittedAt: string;
  totalHours: number;
};
type TimesheetState = {
  allocations: TimesheetAllocationTemplate[];
  weeklyRows: Record<string, TimesheetRow[]>;
  removedEntryTypes: Record<string, string>;
  submittedWeeks: Record<string, SubmittedWeek>;
};
type AllocationFields = Pick<TimesheetAllocationTemplate, 'billable' | 'projectId' | 'department' | 'category'>;
type SeedAllocation = AllocationFields & {
  notes: string;
  hours: Partial<Record<DayKey, string>>;
};
const DEFAULT_USER_ID = 'user-matthew';
const ACTIVE_PROJECTS: ProjectOption[] = INITIAL_JOBS.filter(job => job.status === 'ongoing').map(job => ({
  id: job.id,
  ref: job.ref,
  name: job.name,
  client: job.client,
  departments: job.departments
}));
export function Timesheets() {
  useScreenInit();
  const {
    departments,
    users
  } = useSettingsStore();
  const activeUsers = users.filter(user => user.status === 'active');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [isAddAllocationOpen, setIsAddAllocationOpen] = useState(false);
  const [entryNoteToEdit, setEntryNoteToEdit] = useState<TimesheetRow | null>(null);
  const [entryTypeToRemove, setEntryTypeToRemove] = useState<TimesheetRow | null>(null);
  const [selectedUserId, setSelectedUserId] = useState(DEFAULT_USER_ID);
  const [timesheetsByUser, setTimesheetsByUser] = useState<Record<string, TimesheetState>>(() => ({
    [DEFAULT_USER_ID]: createInitialTimesheetState(ACTIVE_PROJECTS)
  }));
  const selectedUser = activeUsers.find(user => user.id === selectedUserId) || activeUsers[0];
  const activeDepartments = departments.filter(department => department.status === 'active').map(department => department.name);
  const weekKey = dateKey(weekStart);
  const timesheetState = timesheetsByUser[selectedUser?.id || selectedUserId] || createInitialTimesheetState(ACTIVE_PROJECTS);
  const rows = timesheetState.weeklyRows[weekKey] || [];
  const submittedWeek = timesheetState.submittedWeeks[weekKey];
  const days = useMemo(() => createWeekDays(weekStart), [weekStart]);
  const dayTotals = days.map(day => rows.reduce((total, row) => total + parseHours(row.hours[day.key]), 0));
  const weeklyTotal = dayTotals.reduce((total, hours) => total + hours, 0);
  const updateSelectedTimesheet = (updater: (current: TimesheetState) => TimesheetState) => {
    const userId = selectedUser?.id || selectedUserId;
    setTimesheetsByUser(current => ({
      ...current,
      [userId]: updater(current[userId] || createInitialTimesheetState(ACTIVE_PROJECTS))
    }));
  };
  useEffect(() => {
    if (!selectedUser) return;
    setTimesheetsByUser(current => current[selectedUser.id] ? current : {
      ...current,
      [selectedUser.id]: createInitialTimesheetState(ACTIVE_PROJECTS)
    });
  }, [selectedUser]);
  useEffect(() => {
    updateSelectedTimesheet(current => {
      if (current.weeklyRows[weekKey]) return current;
      const generatedRows = current.allocations.filter(allocation => weekKey > allocation.startsFromWeek && !isEntryTypeRemoved(current.removedEntryTypes, allocation, weekKey)).map(allocation => createRow(allocation, '', {}, allocation.id));
      return {
        ...current,
        weeklyRows: {
          ...current.weeklyRows,
          [weekKey]: generatedRows
        }
      };
    });
  }, [weekKey, selectedUserId]);
  useEffect(() => {
    if (!feedback) return;
    const timeout = window.setTimeout(() => setFeedback(null), 3500);
    return () => window.clearTimeout(timeout);
  }, [feedback]);
  const selectUser = (userId: string) => {
    setSelectedUserId(userId);
    setEntryNoteToEdit(null);
    setEntryTypeToRemove(null);
    setFeedback(null);
  };
  const updateRows = (updater: (current: TimesheetRow[]) => TimesheetRow[]) => {
    updateSelectedTimesheet(current => ({
      ...current,
      weeklyRows: {
        ...current.weeklyRows,
        [weekKey]: updater(current.weeklyRows[weekKey] || [])
      }
    }));
    setFeedback({
      tone: 'saved',
      message: 'Saved locally'
    });
  };
  const addPinnedAllocation = (allocation: AllocationFields) => {
    const alreadyPinned = timesheetState.allocations.some(item => allocationKey(item) === allocationKey(allocation));
    if (alreadyPinned) {
      setFeedback({
        tone: 'saved',
        message: 'That pinned entry type is already available'
      });
      setIsAddAllocationOpen(false);
      return;
    }
    const pinnedAllocation: TimesheetAllocationTemplate = {
      id: createId(),
      ...allocation,
      startsFromWeek: weekKey
    };
    updateSelectedTimesheet(current => {
      const entryTypeKey = allocationKey(pinnedAllocation);
      const removedEntryTypes = {
        ...current.removedEntryTypes
      };
      delete removedEntryTypes[entryTypeKey];
      return {
        allocations: [...current.allocations, pinnedAllocation],
        removedEntryTypes,
        weeklyRows: {
          ...current.weeklyRows,
          [weekKey]: [...(current.weeklyRows[weekKey] || []), createRow(pinnedAllocation, '', {}, pinnedAllocation.id)]
        }
      };
    });
    setIsAddAllocationOpen(false);
    setFeedback({
      tone: 'saved',
      message: `Pinned ${allocation.billable ? 'billable' : 'non-billable'} entry type added with blank hours`
    });
  };
  const requestEditEntryNote = (rowId: string) => {
    const row = rows.find(item => item.id === rowId);
    if (row) setEntryNoteToEdit(row);
  };
  const requestRemoveEntryType = (rowId: string) => {
    const row = rows.find(item => item.id === rowId);
    if (row?.templateId) setEntryTypeToRemove(row);
  };
  const confirmRemoveEntryType = () => {
    if (!entryTypeToRemove?.templateId) return;
    const {
      id: rowId,
      templateId
    } = entryTypeToRemove;
    updateSelectedTimesheet(current => {
      const entryTypeKey = allocationKey(entryTypeToRemove);
      return {
        allocations: current.allocations.filter(allocation => allocation.id !== templateId),
        removedEntryTypes: {
          ...current.removedEntryTypes,
          [entryTypeKey]: weekKey
        },
        weeklyRows: Object.fromEntries(Object.entries(current.weeklyRows).map(([key, weekRows]) => {
          if (key === weekKey) return [key, weekRows.filter(item => item.id !== rowId)];
          if (key > weekKey) return [key, weekRows.filter(item => allocationKey(item) !== entryTypeKey)];
          return [key, weekRows.map(item => item.templateId === templateId ? {
            ...item,
            templateId: undefined
          } : item)];
        }))
      };
    });
    setEntryTypeToRemove(null);
    setFeedback({
      tone: 'saved',
      message: 'Entry type removed from this and future weeks'
    });
  };
  const copyPreviousWeek = () => {
    const previousWeekRows = timesheetState.weeklyRows[dateKey(shiftWeek(weekStart, -1))] || [];
    updateSelectedTimesheet(current => ({
      ...current,
      weeklyRows: {
        ...current.weeklyRows,
        [weekKey]: previousWeekRows.filter(row => !isEntryTypeRemoved(current.removedEntryTypes, row, weekKey)).map(row => ({
          ...row,
          id: createId(),
          hours: {
            ...row.hours
          }
        }))
      }
    }));
    setFeedback({
      tone: 'saved',
      message: 'Copied the previous week locally'
    });
  };
  const exportTimesheet = () => {
    downloadCsv<TimesheetRow>(`timesheet-${weekKey}.csv`, [{
      label: 'User',
      value: () => selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''
    }, {
      label: 'Week starting',
      value: () => weekKey
    }, {
      label: 'Entry type',
      value: row => row.billable ? ACTIVE_PROJECTS.find(project => project.id === row.projectId)?.name || 'Project unavailable' : row.category
    }, {
      label: 'Billable',
      value: row => row.billable ? 'Yes' : 'No'
    }, {
      label: 'Project ref',
      value: row => ACTIVE_PROJECTS.find(project => project.id === row.projectId)?.ref
    }, {
      label: 'Client',
      value: row => ACTIVE_PROJECTS.find(project => project.id === row.projectId)?.client
    }, {
      label: 'Department',
      value: row => row.department
    }, {
      label: 'Notes',
      value: row => row.notes
    }, ...WEEKDAY_KEYS.map(day => ({
      label: day.charAt(0).toUpperCase() + day.slice(1),
      value: (row: TimesheetRow) => parseHours(row.hours[day])
    })), {
      label: 'Total hours',
      value: row => getRowTotal(row)
    }], rows);
  };
  return <div className="flex h-screen w-screen overflow-hidden font-sans" style={{
    background: 'linear-gradient(to bottom, #16486B 0%, #0F3D5C 45%, #082A40 100%)'
  }} data-id="element-2052" data-mp="35suxodi25xjp">
    <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} data-id="element-2053" data-mp="3fpzmym77a94q" />
    <main className="flex min-w-0 flex-1 overflow-hidden p-0 md:py-3 md:pr-3" data-id="element-2054" data-mp="3fnwln60lktkp"><div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:shadow-xl" data-id="element-2055" data-mp="3aq4wgjrc7lk7">
      <header className="border-b border-slate-100 px-5 py-5 md:px-8 md:py-7" data-id="element-2056" data-mp="3op4jjmz7bse9"><button type="button" onClick={() => setSidebarOpen(true)} className="mb-4 flex items-center gap-2 text-[#14466B] md:hidden" aria-label="Open menu" data-id="element-2057" data-mp="3kpvrcmjt82kt"><MenuIcon className="h-6 w-6" data-id="element-2058" data-mp="2d7cbot7gqph" /></button><div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between" data-id="element-2059" data-mp="3tgw0d5sk2jr7"><div className="flex items-start gap-3" data-id="element-2060" data-mp="c9aeguo24pcf"><span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#14466B]" data-id="element-2061" data-mp="cabznryses7v"><Clock3Icon className="h-5 w-5" data-id="element-2062" data-mp="7bhbeai1wbgd" /></span><div data-id="element-2063" data-mp="78cz0pufm9em"><h1 className="text-2xl font-bold text-slate-900" data-id="element-2064" data-mp="3v0f9dg8xirpx">Time Sheets</h1><p className="mt-1 text-sm text-slate-500" data-id="element-2065" data-mp="3v1qb7aitdpee">Log billable project time and non-billable leave or internal time.</p></div></div><div className="flex flex-wrap items-center gap-2" data-id="element-2066" data-mp="3q7vsoaey3ggw"><button type="button" onClick={() => setIsAddAllocationOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#14466B] px-3.5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#0F3D5C] focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2" data-id="element-2067" data-mp="3lbnk5fv96lip"><PlusIcon className="h-4 w-4" data-id="element-2068" data-mp="cvvjh4h8apzu" />Add new entry type</button><button type="button" onClick={copyPreviousWeek} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-2069" data-mp="8t77wp14r4z6"><CopyIcon className="h-4 w-4" data-id="element-2070" data-mp="2tkr8gikximx0" />Copy previous week</button><button type="button" onClick={exportTimesheet} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100" title="Export this user's selected week as CSV" data-id="element-2071" data-mp="2ok3gnuovm21q"><DownloadIcon className="h-4 w-4" data-id="element-2072" data-mp="1d27c6iluasuk" />Export CSV</button></div></div></header>
      <section className="flex flex-1 flex-col overflow-y-auto px-5 py-5 md:px-8 md:py-6" data-id="element-2073" data-mp="1htz715c13kqi"><div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-2074" data-mp="1mtbotbo1m1m8"><div className="inline-flex w-fit items-center rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-id="element-2075" data-mp="1rrmztpq2qi61"><button type="button" onClick={() => setWeekStart(current => shiftWeek(current, -1))} className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Show previous week" data-id="element-2076" data-mp="1wr9em3ljw6gy"><ChevronLeftIcon className="h-4 w-4" data-id="element-2077" data-mp="2ejlho30b1am" /></button><div className="min-w-[220px] px-2 text-center" data-id="element-2078" data-mp="6eae7xc3frhs"><p className="text-sm font-semibold text-slate-900" data-id="element-2079" data-mp="6f2brdp93zap">{formatWeekLabel(weekStart)}</p><label className="mt-0.5 flex cursor-pointer items-center justify-center gap-1 text-xs text-[#14466B]" htmlFor="timesheet-user" data-id="element-2080" data-mp="19qizie1y3xd3"><UsersRoundIcon className="h-3.5 w-3.5" data-id="element-2081" data-mp="14s7lrs8j52pl" /><span className="sr-only" data-id="element-2082" data-mp="1ipw4ouj1dzs0">Choose a user's timesheet</span><select id="timesheet-user" value={selectedUser?.id || ''} onChange={event => selectUser(event.target.value)} className="max-w-[168px] cursor-pointer appearance-none bg-transparent pr-1 text-center text-xs font-medium text-[#14466B] outline-none hover:underline focus:ring-2 focus:ring-blue-100" data-id="element-2083" data-mp="1irh3iwcdw4vo">{activeUsers.map(user => <option key={user.id} value={user.id} data-id="element-2084" data-mp="22e5aw8hf06ai">{user.firstName} {user.lastName}'s timesheet</option>)}</select></label></div><button type="button" onClick={() => setWeekStart(current => shiftWeek(current, 1))} className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100" aria-label="Show next week" data-id="element-2085" data-mp="1xfakakd7hdfu"><ChevronRightIcon className="h-4 w-4" data-id="element-2086" data-mp="26hbe1798dhcr" /></button></div><span className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-id="element-2087" data-mp="2q1s5u481hinv"><CalendarDaysIcon className="h-4 w-4 text-[#14466B]" data-id="element-2088" data-mp="2uzafjwtvs1wx" /><strong className="font-semibold tabular-nums text-slate-900" data-id="element-2089" data-mp="2h0u5px0q1vak">{weeklyTotal}h</strong>combined this week</span></div>
        {submittedWeek && <div className="mb-4 flex flex-col gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900 sm:flex-row sm:items-center sm:justify-between" data-id="element-2090" data-mp="2l14e1gbzkxsl"><div className="flex items-start gap-2" data-id="element-2091" data-mp="2c1hdsxuzty14"><CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" data-id="element-2092" data-mp="2c2ivvmaffksc" /><div data-id="element-2093" data-mp="1xdmjjd1c7llx"><p className="text-sm font-semibold" data-id="element-2094" data-mp="1xdw94zigzqr3">Timesheet submitted</p><p className="mt-0.5 text-xs text-emerald-800" data-id="element-2095" data-mp="22bxqng08zi0g">Submitted {formatSubmittedAt(submittedWeek.submittedAt)} · {submittedWeek.totalHours}h recorded for this week.</p></div></div><span className="text-xs font-medium text-emerald-700" data-id="element-2096" data-mp="1ocydb4i6qpjl">{selectedUser ? `${selectedUser.firstName}'s timesheet` : 'Current timesheet'}</span></div>}
        <TimesheetGrid rows={rows} projects={ACTIVE_PROJECTS} days={days} onChangeHours={(rowId, day, value) => updateRows(current => current.map(row => row.id === rowId ? {
            ...row,
            hours: {
              ...row.hours,
              [day]: value
            }
          } : row))} onEditNote={requestEditEntryNote} onRemoveEntryType={requestRemoveEntryType} data-id="element-2097" data-mp="1latl672ytb12" />
        <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-2098" data-mp="17i40c5z6ac5j"><div className="min-h-5" aria-live="polite" data-id="element-2099" data-mp="12ln932wofho8">{feedback && <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${feedback.tone === 'submitted' ? 'text-emerald-700' : 'text-slate-500'}`} data-id="element-2100" data-mp="xkg3nai0aukq"><CheckCircle2Icon className="h-4 w-4" data-id="element-2101" data-mp="smehggpaaf51" />{feedback.message}</span>}</div>{submittedWeek ? <span className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700" data-id="element-2102" data-mp="olm7tmci2888"><CheckCircle2Icon className="h-4 w-4" data-id="element-2103" data-mp="2ixsm4u0ox54r" />Submitted</span> : <button type="button" onClick={() => {
              updateSelectedTimesheet(current => ({
                ...current,
                submittedWeeks: {
                  ...current.submittedWeeks,
                  [weekKey]: {
                    submittedAt: new Date().toISOString(),
                    totalHours: weeklyTotal
                  }
                }
              }));
              setFeedback({
                tone: 'submitted',
                message: `${selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}'s` : 'This'} week submitted with ${weeklyTotal} combined hours`
              });
            }} disabled={weeklyTotal === 0} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#14466B] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#0F3D5C] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400" data-id="element-2104" data-mp="2iw7qt26jxi5u"><SendIcon className="h-4 w-4" data-id="element-2105" data-mp="3qwzmwsggadsj" />Submit week</button>}</div>
      </section>
    </div></main>
    <AnimatePresence data-id="element-2106" data-mp="3vxnepgci6ynt">{isAddAllocationOpen && <PinnedAllocationDialog projects={ACTIVE_PROJECTS} departments={activeDepartments} onClose={() => setIsAddAllocationOpen(false)} onSave={addPinnedAllocation} data-id="element-2107" data-mp="3vuhsc8v9eavs" />}{entryNoteToEdit && <EntryNoteDialog entryDescription={entryNoteToEdit.billable ? `${ACTIVE_PROJECTS.find(project => project.id === entryNoteToEdit.projectId)?.name || 'Project unavailable'} · ${entryNoteToEdit.department || 'No department'}` : entryNoteToEdit.category || 'Non-billable'} initialNote={entryNoteToEdit.notes} onClose={() => setEntryNoteToEdit(null)} onSave={note => {
        updateRows(current => current.map(row => row.id === entryNoteToEdit.id ? {
          ...row,
          notes: note
        } : row));
        setEntryNoteToEdit(null);
      }} data-id="element-2108" data-mp="1b4sf20a7ex57" />}{entryTypeToRemove && <RemoveEntryTypeDialog entryDescription={entryTypeToRemove.billable ? `${ACTIVE_PROJECTS.find(project => project.id === entryTypeToRemove.projectId)?.name || 'Project unavailable'} · ${entryTypeToRemove.department || 'No department'}` : entryTypeToRemove.category || 'Non-billable'} onClose={() => setEntryTypeToRemove(null)} onConfirm={confirmRemoveEntryType} data-id="element-2109" data-mp="2jkgvpr63e1c1" />}</AnimatePresence>
  </div>;
}
function createInitialTimesheetState(projects: ProjectOption[]): TimesheetState {
  const thisWeek = startOfWeek(new Date());
  const previousWeek = shiftWeek(thisWeek, -1);
  const first = projects[0];
  const second = projects[1];
  const currentRows: SeedAllocation[] = [...(first ? [{
    billable: true,
    projectId: first.id,
    department: 'Surveying',
    notes: 'Site survey and preparation',
    hours: {
      mon: '4',
      tue: '7.5',
      wed: '6',
      thu: '7.5',
      fri: '4'
    }
  }] : []), ...(second ? [{
    billable: true,
    projectId: second.id,
    department: 'Design',
    notes: 'Design review',
    hours: {
      wed: '2',
      thu: '1.5',
      fri: '4'
    }
  }] : []), {
    billable: false,
    category: 'Admin',
    notes: 'Internal administration',
    hours: {
      mon: '4',
      wed: '2',
      thu: '1.5'
    }
  }, {
    billable: false,
    category: 'CPD',
    notes: '',
    hours: {}
  }, {
    billable: false,
    category: 'Sick leave',
    notes: '',
    hours: {}
  }, {
    billable: false,
    category: 'Annual leave',
    notes: '',
    hours: {}
  }];
  const pinnedProjectIds = new Set(currentRows.filter(row => row.billable).map(row => row.projectId));
  projects.forEach(project => {
    if (!pinnedProjectIds.has(project.id)) currentRows.push({
      billable: true,
      projectId: project.id,
      department: project.departments[0] || 'Project Delivery',
      notes: '',
      hours: {}
    });
  });
  const previousRows: SeedAllocation[] = [...(first ? [{
    billable: true,
    projectId: first.id,
    department: 'Surveying',
    notes: 'Survey coordination',
    hours: {
      mon: '7.5',
      tue: '7.5',
      wed: '7.5',
      thu: '7.5',
      fri: '4'
    }
  }] : []), {
    billable: false,
    category: 'Annual leave',
    notes: 'Annual leave',
    hours: {
      fri: '3.5'
    }
  }];
  const templatesByKey = new Map<string, TimesheetAllocationTemplate>();
  [...currentRows, ...previousRows].forEach(row => {
    const key = allocationKey(row);
    if (!templatesByKey.has(key)) templatesByKey.set(key, {
      id: createId(),
      billable: row.billable,
      projectId: row.projectId,
      department: row.department,
      category: row.category,
      startsFromWeek: dateKey(thisWeek)
    });
  });
  const templateIdFor = (row: SeedAllocation) => templatesByKey.get(allocationKey(row))?.id;
  return {
    allocations: Array.from(templatesByKey.values()),
    removedEntryTypes: {},
    submittedWeeks: {},
    weeklyRows: {
      [dateKey(thisWeek)]: currentRows.map(row => createRow(row, row.notes, row.hours, templateIdFor(row))),
      [dateKey(previousWeek)]: previousRows.map(row => createRow(row, row.notes, row.hours, templateIdFor(row)))
    }
  };
}
function createRow(allocation: AllocationFields, notes = '', hours: Partial<Record<DayKey, string>> = {}, templateId?: string): TimesheetRow {
  return {
    id: createId(),
    ...allocation,
    notes,
    hours: {
      ...emptyHours(),
      ...hours
    },
    templateId
  };
}
function allocationKey({
  billable,
  projectId,
  department,
  category
}: AllocationFields) {
  return billable ? `billable::${projectId || ''}::${department || ''}` : `non-billable::${category || ''}`;
}
function isEntryTypeRemoved(removedEntryTypes: Record<string, string>, entryType: AllocationFields, targetWeek: string) {
  const removedFromWeek = removedEntryTypes[allocationKey(entryType)];
  return Boolean(removedFromWeek && targetWeek >= removedFromWeek);
}
function createWeekDays(weekStart: Date) {
  const today = dateKey(new Date());
  return WEEKDAY_KEYS.map((key, index) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + index);
    return {
      key,
      shortLabel: date.toLocaleDateString('en-GB', {
        weekday: 'short'
      }),
      dateLabel: date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      }),
      isToday: dateKey(date) === today
    };
  });
}
function startOfWeek(date: Date) {
  const next = new Date(date);
  const day = next.getDay();
  next.setDate(next.getDate() + (day === 0 ? -6 : 1 - day));
  next.setHours(0, 0, 0, 0);
  return next;
}
function shiftWeek(date: Date, amount: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount * 7);
  return next;
}
function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function formatSubmittedAt(value: string) {
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
function formatWeekLabel(weekStart: Date) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
  const start = weekStart.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: sameMonth ? undefined : 'short',
    year: sameYear ? undefined : 'numeric'
  });
  const end = weekEnd.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return `${start} – ${end}`;
}
function createId() {
  return `time-${Math.random().toString(36).slice(2, 10)}`;
}